import { useRef, useEffect, useCallback, useState } from 'react'
import { VirtuosoHandle, StateSnapshot } from 'react-virtuoso'
import _ from 'lodash'
import { DisplayRoomMessage, DisplayThreadMessage } from '@evertel/stores'

// Module-level variable to store state snapshots
const globalStateSnapshots: { [key: string]: StateSnapshot | undefined } = {}

interface VirtuosoScrollSaveStateProps {
    key: string
    messages: (DisplayRoomMessage | DisplayThreadMessage)[]
    focusedMessageId?: number | null
    setFocusedMessageId?: (id: number | null) => void
    debounceMs?: number
}

export function useVirtuosoScrollSaveState({
    key,
    messages,
    focusedMessageId,
    setFocusedMessageId,
    debounceMs = 200
}: VirtuosoScrollSaveStateProps) {

    const virtuosoRef = useRef<VirtuosoHandle>(null)
    const scrollerRef = useRef<HTMLElement | null>(null)

    const saveStateRef = useRef(null)
    const previousFocusedMessageId = useRef(null)

    const [isScrollerReady, setIsScrollerReady] = useState(false)

    useEffect(() => {
        if (messages.length > 0) {
            // virtuosoRef.current?.scrollToIndex({
            //     index: messages.length - 1,
            //     behavior: 'auto',
            //     align: 'end'
            // })
        }
    }, [messages])

    const findAndFocusLowestVisibleMessage = useCallback(() => {
        const messageElements = document.querySelectorAll('[data-message-holder]')
        const visibleMessages: { element: Element, index: number }[] = []

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const index = Array.from(messageElements).indexOf(entry.target)
                    visibleMessages.push({
                        element: entry.target,
                        index: index
                    })
                }
            })

            // Once we have all results, find lowest visible and focus it
            const lowestMessage = visibleMessages.sort((a, b) => a.index - b.index).pop()
            if (lowestMessage) {
                const messageId = lowestMessage.element.id.replace('message-id-', '')
                setFocusedMessageId(Number(messageId));
                (lowestMessage.element as HTMLElement).focus()
            }
            observer.disconnect()
        }, {
            root: document.getElementById('virtuoso-scroller'),
            threshold: 0.2 // Consider element visible when at least 20% is in view
        })

        // Observe all message elements at once
        messageElements.forEach(element => observer.observe(element))
    }, [setFocusedMessageId])

    const handleVirtuosoFocus = useCallback((e: React.FocusEvent) => {
        const virtuosoRoot = scrollerRef.current
        if (!virtuosoRoot) return

        const relatedTargetInside = virtuosoRoot.contains(e.relatedTarget as Node)
        const isExternalFocus = !e.relatedTarget || !relatedTargetInside

        // Handle direct virtuoso root focus
        if (virtuosoRoot === e.target) {
            if (relatedTargetInside) {
                // Focus leaving from top
                // I kinda hate this, but... :/
                // TODO: alternative, make virtuoso tabindex={-1} when focus is inside, but also more error prone
                const prevElementToMessageList = document.getElementById('reply-header-back-button') ||
                                document.getElementById('header-dropdown-profile-settings-menu')
                if (prevElementToMessageList) {
                    //without this there is 1 extra empty "tab" state
                    prevElementToMessageList.focus()
                }
                return
            }
            // Note: Focus potentially entering but often not triggered
        }

        // Only proceed if focus is coming from outside
        if (!isExternalFocus) return

        const currentFocusedMessageId = focusedMessageId || previousFocusedMessageId.current
        setFocusedMessageId(currentFocusedMessageId)

        // First try to focus the currently selected message if it exists
        const currentElement = currentFocusedMessageId
            ? document.getElementById(`message-id-${currentFocusedMessageId}`)
            : null

        // If no focused message, fall back to lowest visible
        if (!currentElement) {
            findAndFocusLowestVisibleMessage()
            return
        }

        if (currentElement.contains(e.target)) {
            //let natural event happen
            return
        }

        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0]
            if (entry.isIntersecting) {
                currentElement?.focus()
            } else {
                findAndFocusLowestVisibleMessage()
            }
            observer.disconnect()
        }, {
            root: document.getElementById('virtuoso-scroller'),
            threshold: 0.2 // Consider element visible when at least 20% is in view
        })

        observer.observe(currentElement)

    }, [focusedMessageId, findAndFocusLowestVisibleMessage])

    const handleVirtuosoBlur = useCallback((e: React.FocusEvent) => {
        const virtuosoRoot = scrollerRef.current
        if (!virtuosoRoot) return

        const relatedTargetInside = e.relatedTarget && virtuosoRoot.contains(e.relatedTarget as Node)
        // Check if the related target is an emoji picker or modal that should preserve focus
        const isEmojiPicker = e.relatedTarget && (
            (e.relatedTarget as HTMLElement).tagName?.toLowerCase() === 'em-emoji-picker'
            || (e.relatedTarget as HTMLElement).closest('em-emoji-picker')
        )

        const isModal = e.relatedTarget && (
            (e.relatedTarget as HTMLElement).classList?.contains('modal')
            || (e.relatedTarget as HTMLElement).closest('.modal')
        )

        // If focus is leaving the virtuoso container entirely, but not to emoji picker or modal
        if (!relatedTargetInside && !isEmojiPicker && !isModal) {
        // Save the current focused message ID
            console.log('RELATED', e.relatedTarget)
            if (focusedMessageId) {
                previousFocusedMessageId.current = focusedMessageId
            }
            // Clear the current focused message ID
            if (setFocusedMessageId) {
                setFocusedMessageId(null)
            }
        }
    }, [focusedMessageId, setFocusedMessageId])

    const saveState = useCallback(() => {
        if (virtuosoRef.current) {
            virtuosoRef.current.getState((snapshot) => {
                globalStateSnapshots[key] = snapshot
                console.log(':::::: State saved for:', key)
            })
        }
    }, [key])

    useEffect(() => {
        // always have the latest reference to save here to call in debounce
        saveStateRef.current = saveState
    }, [saveState])

    const debouncedSaveState = useRef(
        // don't want debouncedSearch to be recreated or it ruins the debounce
        _.debounce(() => {
            if (saveStateRef.current) {
                saveStateRef.current()
            }
        }, debounceMs)
    ).current

    useEffect(() => {
        const currentScrollerRef = scrollerRef.current
        if (currentScrollerRef) {
            currentScrollerRef.addEventListener('scroll', debouncedSaveState)
        }

        return () => {
            if (currentScrollerRef) {
                currentScrollerRef.removeEventListener('scroll', debouncedSaveState)
            }
            debouncedSaveState.cancel()
        }
    }, [debouncedSaveState, isScrollerReady])

    const setScrollerRef = useCallback((ref: HTMLElement | null) => {
        scrollerRef.current = ref
        setIsScrollerReady(!!ref) // Trigger effect when ref is set
    }, [])

    const getRestoreState = useCallback(() => {
        return globalStateSnapshots[key]
    }, [key])

    /**
     * Note: indexes are in opposite order, lowest index is top of list
     */
    const keyDownCallback = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (!focusedMessageId) return

            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault()
                e.stopPropagation()

                const maxIndex = messages.length - 1

                const currentIndex = messages.findIndex(msg => msg.id === focusedMessageId)
                let nextIndex = currentIndex

                if (e.key === 'ArrowUp') {
                    nextIndex = Math.max(0, currentIndex - 1)
                } else if (e.key === 'ArrowDown') {
                    // If we're at the last message and trying to go down
                    if (currentIndex === maxIndex) {
                        const messageInput = document.getElementById('message-input')
                        if (messageInput) {
                            messageInput?.focus()
                            return
                        }
                    }
                    nextIndex = Math.min(maxIndex, currentIndex + 1)
                }
                if (nextIndex !== currentIndex) {
                    const nextMessage = messages[nextIndex]
                    setFocusedMessageId(nextMessage.id)

                    const messageElement = document.getElementById(`message-id-${nextMessage.id}`)
                    messageElement?.focus()
                    virtuosoRef.current?.scrollIntoView({
                        index: nextIndex,
                        behavior: 'auto',
                        // align: 'center',
                        done: () => {
                            // this is too slow, causes weird lag
                            // messageElement?.focus()
                        }
                    })
                }
            }
        },
        [focusedMessageId, messages, virtuosoRef]
    )
    return {
        virtuosoRef,
        setScrollerRef,
        getRestoreState,
        saveState,
        keyDownCallback,
        handleVirtuosoFocus,
        handleVirtuosoBlur
    }
}
